/**
 * Everything behind an open dialog is `inert`, counted
 * (jorigin/brewauth#29 GB8).
 *
 * Why not `aria-modal`: the people-and-groups picker (`@brew/ui`'s
 * `ActorPicker`, through `ActorSearch` and the Add-members drawer) renders
 * its popup into `document.body`, so it escapes the dialog's scroll box.
 * `aria-modal="true"` tells assistive technology that everything outside the
 * dialog is unavailable, and the popup IS outside it: its listbox would be
 * hidden with the page. `@brew/ui` 0.18.0's `portal` option was a boolean
 * (body or nothing), and a popup placed inside the dialog could not be
 * positioned there anyway: `.brew-modal`'s `backdrop-filter` makes it the
 * containing block for the popup's `position: fixed`. So no dialog here sets
 * `aria-modal`; instead, while one is open, every other child of
 * `document.body` that exists at that moment (the app root, a dialog it was
 * opened over) is marked `inert`. A picker popup or a dialog opened later
 * is a later child, so it is not.
 *
 * Since `@brew/ui` 0.19.0 (`portal` takes a container, and places the popup
 * in that container's frame) the pickers inside the sharing dialog, the pane
 * modal and the Add-members drawer portal into their OWN dialog's root, so
 * their popups are no longer body children at all. This module is unchanged
 * until a real browser confirms the popups are placed right there; what that
 * would let go is listed in jorigin/brewauth#29's 0.19.0 adoption report.
 *
 * Exempt: a body child marked `data-inert-exempt` is never marked. The
 * toast layer (`components/layout/ToastLayer.tsx`) is one: a toast raised
 * while a dialog is open must still be announced, and its buttons work.
 *
 * Dialogs opened in the SAME commit are not supported: the first to claim
 * marks the other's root inert, then the other marks the first's, and both
 * are dead. Like `escapeLayers`, this relies on a stacked dialog opening in a
 * render of its own (a click opens it). A dialog whose own root is already
 * inert when it claims is that case; in development it is reported with
 * `console.error` so it surfaces instead of shipping as a dead UI.
 *
 * Counted like `lockBodyScroll`: dialogs stack, and each element stays inert
 * until the last dialog that marked it releases it. An element that was
 * already `inert` before any dialog opened is left that way.
 *
 * Focus: marking the app root inert takes focus off an opener inside it (a
 * dialog that moves focus in keeps it). On release, if focus was left on
 * nothing, it goes back to the element that had it when this dialog opened,
 * when that is still in the page and no longer inert.
 *
 * "When this dialog opened" must be read BEFORE the dialog's content
 * mounts: React applies `autoFocus` while committing, before any effect of
 * the dialog runs, so an effect reading `document.activeElement` finds the
 * dialog's own Cancel or Name field, not its opener, and on close has
 * nothing left to go back to (GB final, I3). A dialog whose content may
 * autofocus passes the opener it recorded while rendering (`restoreTo`, as
 * `Modal` does); without one, the element focused when `inertBackground`
 * runs is used.
 */
import { useEffect, type RefObject } from 'react';

interface Mark {
  count: number;
  /** The element was `inert` before any dialog marked it: never cleared here. */
  preexisting: boolean;
}

const marks = new Map<Element, Mark>();

/** The child of `document.body` that holds `el` (its portal root), if any. */
function bodyChildOf(el: Element): Element | null {
  let n: Element | null = el;
  while (n && n.parentElement !== document.body) n = n.parentElement;
  return n;
}

function isInert(el: Element): boolean {
  return el.closest('[inert]') !== null;
}

/**
 * Mark every other child of `document.body` inert while `dialog` is open;
 * call the returned function to release this dialog's marks. `restoreTo` is
 * where focus goes back to on release (when focus was left on nothing); it
 * defaults to the element focused now.
 */
export function inertBackground(dialog: Element, restoreTo?: Element | null): () => void {
  const own = bodyChildOf(dialog);
  if (!own) return () => {};
  if (import.meta.env.DEV && marks.has(own)) {
    console.error(
      'inertBackground: this dialog opened with its own root already inert. ' +
        'Two dialogs mounted in the same commit inert each other; open the ' +
        'stacked one in a render of its own.',
      dialog,
    );
  }
  const previousFocus = restoreTo === undefined ? document.activeElement : restoreTo;
  const marked: Element[] = [];
  for (const child of Array.from(document.body.children)) {
    if (child === own || child.hasAttribute('data-inert-exempt')) continue; // NOSONAR typescript:S7761 refactor-deferred: checks data-inert-exempt by its markup name, the attribute name callers put on exempt elements
    const mark = marks.get(child);
    if (mark) {
      mark.count += 1;
    } else {
      marks.set(child, { count: 1, preexisting: child.hasAttribute('inert') });
      child.setAttribute('inert', '');
    }
    marked.push(child);
  }
  let released = false;
  return () => {
    if (released) return;
    released = true;
    for (const child of marked) {
      const mark = marks.get(child);
      if (!mark) continue;
      mark.count -= 1;
      if (mark.count === 0) {
        marks.delete(child);
        if (!mark.preexisting) child.removeAttribute('inert');
      }
    }
    const active = document.activeElement;
    if (
      (active === null || active === document.body) &&
      previousFocus instanceof HTMLElement &&
      previousFocus !== document.body &&
      previousFocus.isConnected &&
      !isInert(previousFocus)
    ) {
      previousFocus.focus();
    }
  };
}

/**
 * `inertBackground` for the dialog `ref` points at, while `active`.
 * `restoreTo`: the opener, recorded before the dialog's content could take
 * focus (see the file comment); omitted, the element focused when the
 * effect runs.
 */
export function useInertBackground(
  ref: RefObject<Element | null>,
  active: boolean,
  restoreTo?: Element | null,
): void {
  useEffect(() => {
    const el = ref.current;
    if (!active || !el) return;
    return inertBackground(el, restoreTo);
  }, [ref, active, restoreTo]);
}
